import React, { useState } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, ImageMinus, Palette, Maximize2, FileImage, Copy } from "lucide-react";
import { getErrorMessage } from "../utils/errorHandler";
import { buildWhatsAppSticker, shareStickerOrDownload } from "../utils/stickerHelper";

export default function Sublimacion() {
  const [currentFileId, setCurrentFileId] = useState(null);
  const [currentFilename, setCurrentFilename] = useState("");
  const [processedFilename, setProcessedFilename] = useState("");
  const [originalPreview, setOriginalPreview] = useState(null);
  const [processedPreview, setProcessedPreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [productSizes, setProductSizes] = useState({});
  
  // File upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [backgroundImageFile, setBackgroundImageFile] = useState(null);
  
  // Download format
  const [downloadFormat, setDownloadFormat] = useState("png");
  
  // Background color
  const [bgColor, setBgColor] = useState("#FFFFFF");
  
  // Adjustment options for composite
  const [scale, setScale] = useState(1.0);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [opacity, setOpacity] = useState(100);
  
  // Interactive drag mode
  const [dragMode, setDragMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [canvasPreview, setCanvasPreview] = useState(null);
  const [foregroundImage, setForegroundImage] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [gridSize, setGridSize] = useState(20); // pixels for snap-to-grid
  
  // Canvas ref for interactive drag
  const canvasRef = React.useRef(null);
  
  // Enhancement options
  const [sharpen, setSharpen] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [brightness, setBrightness] = useState(0);
  
  // Resize options
  const [productType, setProductType] = useState("carta");
  const [customWidth, setCustomWidth] = useState(10);
  const [customHeight, setCustomHeight] = useState(10);
  const [dpi, setDpi] = useState(300);

  // AI Background Replacement (Gemini Nano Banana)
  const [aiBgPrompt, setAiBgPrompt] = useState("");
  const [aiBgQuality, setAiBgQuality] = useState("high");
  const [aiBgFormat, setAiBgFormat] = useState("original"); // Output aspect ratio / product format

  // Available output formats for AI background replacement
  const aiBgFormats = [
    { value: 'original', label: '🖼️ Original (sin cambiar proporción)', ratio: null },
    { value: 'square', label: '⬜ Cuadrado 1:1 (Instagram, Facebook)', ratio: 1 / 1 },
    { value: 'portrait', label: '📱 Vertical 9:16 (Status, Reels)', ratio: 9 / 16 },
    { value: 'landscape', label: '🖥️ Horizontal 16:9 (YouTube, web)', ratio: 16 / 9 },
    { value: 'post', label: '📷 Post 4:5 (Instagram feed)', ratio: 4 / 5 },
    { value: 'taza_11oz', label: '☕ Taza 11oz (20 × 9.5 cm horizontal)', ratio: 20 / 9.5 },
    { value: 'taza_15oz', label: '☕ Taza 15oz (22 × 10 cm horizontal)', ratio: 22 / 10 },
    { value: 'mousepad', label: '🖱️ Mousepad (24 × 20 cm)', ratio: 24 / 20 },
    { value: 'camiseta', label: '👕 Camiseta (28 × 35 cm vertical)', ratio: 28 / 35 },
    { value: 'cojin', label: '🛋️ Cojín 40×40 cm (cuadrado)', ratio: 1 / 1 },
    { value: 'a4_h', label: '📄 A4 Horizontal (29.7 × 21 cm)', ratio: 29.7 / 21 },
    { value: 'a4_v', label: '📄 A4 Vertical (21 × 29.7 cm)', ratio: 21 / 29.7 },
  ]; // Default Pro (works reliably with async polling)

  // Quick presets for AI background replacement
  const subBgPresets = [
    { label: '🎂 Cumpleaños niña', prompt: 'Escenografía de cumpleaños para niña: globos rosados y dorados, flores rosadas, oso de peluche, luces cálidas tipo bokeh, alfombra de peluche rosa, marco decorativo dorado' },
    { label: '🎈 Cumpleaños niño', prompt: 'Escenografía de cumpleaños para niño: globos azules y dorados, estrellas, confeti, luces cálidas tipo bokeh, alfombra azul, decoración elegante' },
    { label: '👶 Felices X Meses', prompt: 'Sesión de mes-versario de bebé: fondo crema con globos pastel, flores rosadas y blancas, oso de peluche, luces tipo bokeh dorado, alfombra de peluche, ambiente de estudio profesional' },
    { label: '🎄 Navidad', prompt: 'Escenografía navideña: árbol de Navidad iluminado, regalos envueltos, luces cálidas, copos de nieve, mantel rojo, ambiente cozy de invierno' },
    { label: '🎃 Halloween', prompt: 'Escenografía de Halloween: calabazas talladas, telarañas, hojas otoñales, niebla suave, luces anaranjadas, fondo oscuro misterioso' },
    { label: '💖 Día de la madre', prompt: 'Escenografía día de la madre: ramo de rosas rosadas, corazones, luces cálidas tipo bokeh, fondo color durazno suave, ambiente elegante' },
    { label: '🏖️ Playa tropical', prompt: 'Playa tropical al atardecer: arena dorada, palmeras, mar turquesa al fondo, cielo con tonos naranja y rosa, ambiente cálido y relajado' },
    { label: '🌸 Jardín de flores', prompt: 'Jardín de flores hermoso: rosas, tulipanes y peonías en tonos pastel, luz natural suave del amanecer, mariposas, ambiente romántico' },
    { label: '⚪ Estudio blanco', prompt: 'Fondo de estudio profesional blanco infinito, iluminación suave y uniforme tipo softbox, ideal para producto, sin sombras duras, fondo limpio sin elementos' },
    { label: '⚫ Estudio negro', prompt: 'Fondo de estudio profesional negro liso, iluminación dramática lateral, ideal para producto premium, sin elementos distractores' },
    { label: '🏙️ Ciudad nocturna', prompt: 'Ciudad de noche con luces de neón coloridas, edificios iluminados al fondo desenfocado tipo bokeh, ambiente urbano moderno y vibrante' },
    { label: '🌿 Naturaleza verde', prompt: 'Naturaleza exuberante: bosque con hojas verdes desenfocadas tipo bokeh, rayos de sol filtrándose entre los árboles, ambiente fresco y natural' },
  ];
  
  React.useEffect(() => {
    fetchProductSizes();
  }, []);
  
  const fetchProductSizes = async () => {
    try {
      const response = await axiosInstance.get("/sublimation/products");
      setProductSizes(response.data.products);
    } catch (error) {
      console.error("Error fetching product sizes:", error);
    }
  };
  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview for original
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Por favor selecciona un archivo");
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      const response = await axiosInstance.post("/sublimation/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setCurrentFileId(response.data.file_id);
      setCurrentFilename(response.data.filename);
      toast.success("Imagen subida correctamente");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  // Ensures a fresh file_id exists on backend; re-uploads selectedFile if missing/404.
  const ensureValidFileId = async () => {
    if (!selectedFile) {
      throw new Error("Primero sube una imagen en la pestaña 'Subir'");
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    const response = await axiosInstance.post("/sublimation/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setCurrentFileId(response.data.file_id);
    setCurrentFilename(response.data.filename);
    return response.data.file_id;
  };
  
  const handleRemoveBackground = async () => {
    if (!selectedFile && !currentFileId) {
      toast.error("Primero sube una imagen en la pestaña 'Subir'");
      return;
    }
    
    setProcessing(true);
    try {
      let fileIdToUse = currentFileId;
      const callApi = async (fid) => {
        const formData = new FormData();
        formData.append("file_id", fid);
        return axiosInstance.post("/sublimation/remove-background", formData);
      };

      let response;
      try {
        response = await callApi(fileIdToUse);
      } catch (err) {
        // If file was wiped on server (404), auto re-upload and retry once
        if (err.response?.status === 404 && selectedFile) {
          fileIdToUse = await ensureValidFileId();
          response = await callApi(fileIdToUse);
        } else {
          throw err;
        }
      }
      
      setProcessedPreview(response.data.preview);
      setProcessedFilename(response.data.processed_filename);
      
      // Load foreground image for canvas
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setForegroundImage(img);
      img.src = response.data.preview;
      
      if (response.data.fallback === "gemini") {
        toast.success("Fondo eliminado con Gemini (Remove.bg sin créditos)");
      } else {
        toast.success("Fondo eliminado correctamente");
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveSubject = async () => {
    if (!selectedFile && !currentFileId) {
      toast.error("Primero sube una imagen en la pestaña 'Subir'");
      return;
    }
    
    setProcessing(true);
    try {
      let fileIdToUse = currentFileId;
      const callApi = async (fid) => {
        const formData = new FormData();
        formData.append("file_id", fid);
        return axiosInstance.post("/sublimation/remove-subject", formData);
      };
      let response;
      try {
        response = await callApi(fileIdToUse);
      } catch (err) {
        if (err.response?.status === 404 && selectedFile) {
          fileIdToUse = await ensureValidFileId();
          response = await callApi(fileIdToUse);
        } else { throw err; }
      }
      
      setProcessedPreview(response.data.preview);
      setProcessedFilename(response.data.processed_filename);
      toast.success("Personas/animales eliminados correctamente");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };
  
  
  const handleChangeBackgroundColor = async () => {
    if (!selectedFile && !currentFileId) {
      toast.error("Primero sube una imagen en la pestaña 'Subir'");
      return;
    }
    
    setProcessing(true);
    try {
      let fileIdToUse = currentFileId;
      const callApi = async (fid) => {
        const formData = new FormData();
        formData.append("file_id", fid);
        formData.append("bg_color", bgColor);
        return axiosInstance.post("/sublimation/change-background", formData);
      };
      let response;
      try {
        response = await callApi(fileIdToUse);
      } catch (err) {
        if (err.response?.status === 404 && selectedFile) {
          fileIdToUse = await ensureValidFileId();
          response = await callApi(fileIdToUse);
        } else { throw err; }
      }
      
      setProcessedPreview(response.data.preview);
      setProcessedFilename(response.data.processed_filename);
      toast.success("Fondo cambiado correctamente");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  // Send processed image as WhatsApp sticker (WebP 512x512, <100KB)
  const handleSendAsSticker = async () => {
    if (!processedPreview) {
      toast.error("Primero procesa una imagen");
      return;
    }
    try {
      toast.loading("Convirtiendo a sticker…", { id: "sub-sticker" });
      const file = await buildWhatsAppSticker(processedPreview);
      const sizeKB = (file.size / 1024).toFixed(0);
      const result = await shareStickerOrDownload(file, "Sticker PaliStore");
      toast.dismiss("sub-sticker");
      if (result === "shared") {
        toast.success(`Sticker enviado (${sizeKB} KB)`);
      } else {
        toast.success(`Sticker descargado (${sizeKB} KB). Compártelo en WhatsApp adjuntándolo.`);
      }
    } catch (err) {
      toast.dismiss("sub-sticker");
      toast.error("Error al crear sticker: " + (err.message || "desconocido"));
    }
  };

  // Solo Persona: aislar al sujeto principal (persona/bebé/animal) y quitar todos los objetos
  const handleIsolateSubject = async () => {
    if (!selectedFile) {
      toast.error("Primero sube una imagen en la pestaña 'Subir'");
      return;
    }
    setProcessing(true);
    const loadingToast = toast.loading("IA aislando al sujeto… 20-50 segundos", { duration: Infinity });
    try {
      // Resize input to 1600px max
      const resizedFile = await new Promise((resolve, reject) => {
        const MAX_SIDE = 1600;
        const reader = new FileReader();
        reader.onload = (ev) => {
          const img = new Image();
          img.onload = () => {
            let w = img.width, h = img.height;
            const longest = Math.max(w, h);
            if (longest > MAX_SIDE) {
              const s = MAX_SIDE / longest; w = Math.round(w*s); h = Math.round(h*s);
            }
            const canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0,0,w,h);
            ctx.drawImage(img, 0, 0, w, h);
            canvas.toBlob((blob) => {
              if (!blob) return reject(new Error("No se pudo procesar"));
              resolve(new File([blob], (selectedFile.name || "image").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }));
            }, "image/jpeg", 0.92);
          };
          img.onerror = () => reject(new Error("No se pudo leer la imagen"));
          img.src = ev.target.result;
        };
        reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
        reader.readAsDataURL(selectedFile);
      });

      const formData = new FormData();
      formData.append("image", resizedFile);
      formData.append("prompt", "Isolate only the person/baby/animal subject");
      formData.append("style", "realista");
      formData.append("quality", "high");
      formData.append("mode", "isolate_subject");

      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const submitResp = await fetch(`${apiUrl}/api/arts/modify-async`, { method: "POST", body: formData });
      const submitText = await submitResp.text();
      let submitData = null;
      try { submitData = submitText ? JSON.parse(submitText) : null; } catch (_e) { submitData = null; }
      if (!submitResp.ok || !submitData?.job_id) {
        toast.dismiss(loadingToast);
        toast.error(submitData?.detail || "No se pudo iniciar el procesamiento");
        return;
      }
      const jobId = submitData.job_id;

      let finalData = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const poll = await fetch(`${apiUrl}/api/arts/job/${jobId}`);
          const pollText = await poll.text();
          let pollData = null;
          try { pollData = pollText ? JSON.parse(pollText) : null; } catch (_e) { pollData = null; }
          if (pollData?.status === "done") { finalData = pollData.result; break; }
          if (pollData?.status === "error") {
            toast.dismiss(loadingToast);
            toast.error(pollData.error || "La IA no pudo aislar al sujeto");
            return;
          }
        } catch (_pollErr) { /* keep polling */ }
      }

      toast.dismiss(loadingToast);

      if (finalData && finalData.success) {
        setProcessedPreview(finalData.image_base64);
        setProcessedFilename(finalData.filename);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => setForegroundImage(img);
        img.src = finalData.image_base64;
        toast.success(`¡Solo el sujeto! Objetos eliminados con Gemini`);
      } else {
        toast.error("La IA tardó demasiado. Intenta de nuevo.", { duration: 8000 });
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Error: " + (error.message || "desconocido"));
    } finally {
      setProcessing(false);
    }
  };

  // Reemplazar fondo usando Gemini Nano Banana (mantiene sujeto, construye escena IA)
  const handleReplaceBackgroundAI = async () => {
    if (!selectedFile) {
      toast.error("Primero sube una imagen en la pestaña 'Subir'");
      return;
    }
    if (!aiBgPrompt.trim()) {
      toast.error("Describe el nuevo fondo que quieres");
      return;
    }

    setProcessing(true);
    const loadingToast = toast.loading("IA procesando… esto puede tardar 20-50 segundos", { duration: Infinity });
    try {
      const targetFormat = aiBgFormats.find(f => f.value === aiBgFormat) || aiBgFormats[0];
      const targetRatio = targetFormat.ratio;

      const resizedFile = await new Promise((resolve, reject) => {
        const MAX_SIDE = 1600;
        const reader = new FileReader();
        reader.onload = (ev) => {
          const img = new Image();
          img.onload = () => {
            let canvasW, canvasH;
            if (targetRatio) {
              if (targetRatio >= 1) {
                canvasW = MAX_SIDE;
                canvasH = Math.round(MAX_SIDE / targetRatio);
              } else {
                canvasH = MAX_SIDE;
                canvasW = Math.round(MAX_SIDE * targetRatio);
              }
            } else {
              let w = img.width;
              let h = img.height;
              const longest = Math.max(w, h);
              if (longest > MAX_SIDE) {
                const scale = MAX_SIDE / longest;
                w = Math.round(w * scale);
                h = Math.round(h * scale);
              }
              canvasW = w;
              canvasH = h;
            }

            const canvas = document.createElement("canvas");
            canvas.width = canvasW;
            canvas.height = canvasH;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, canvasW, canvasH);

            if (targetRatio) {
              const imgRatio = img.width / img.height;
              const canvasRatio = canvasW / canvasH;
              let drawW, drawH, dx, dy;
              if (imgRatio > canvasRatio) {
                drawW = canvasW;
                drawH = canvasW / imgRatio;
                dx = 0;
                dy = (canvasH - drawH) / 2;
              } else {
                drawH = canvasH;
                drawW = canvasH * imgRatio;
                dx = (canvasW - drawW) / 2;
                dy = 0;
              }
              ctx.drawImage(img, dx, dy, drawW, drawH);
            } else {
              ctx.drawImage(img, 0, 0, canvasW, canvasH);
            }

            canvas.toBlob((blob) => {
              if (!blob) return reject(new Error("No se pudo procesar la imagen"));
              resolve(new File([blob], (selectedFile.name || "image").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }));
            }, "image/jpeg", 0.92);
          };
          img.onerror = () => reject(new Error("No se pudo leer la imagen"));
          img.src = ev.target.result;
        };
        reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
        reader.readAsDataURL(selectedFile);
      });

      let promptWithFormat = aiBgPrompt.trim();
      if (targetRatio) {
        promptWithFormat += `. IMPORTANT: Fill the entire white/empty area around the subject with the new background scene, maintaining the ${targetFormat.label} composition. The subject should remain in the center, the scene should extend naturally to fill the full ${targetRatio >= 1 ? 'horizontal' : 'vertical'} canvas.`;
      }

      const formData = new FormData();
      formData.append("image", resizedFile);
      formData.append("prompt", promptWithFormat);
      formData.append("style", "realista");
      formData.append("quality", aiBgQuality);
      formData.append("mode", "replace_background");

      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // 1) Submit job - returns immediately with job_id
      const submitResp = await fetch(`${apiUrl}/api/arts/modify-async`, {
        method: "POST",
        body: formData,
      });
      const submitText = await submitResp.text();
      let submitData = null;
      try { submitData = submitText ? JSON.parse(submitText) : null; } catch (_e) { submitData = null; }

      if (!submitResp.ok || !submitData?.job_id) {
        toast.dismiss(loadingToast);
        toast.error(submitData?.detail || "No se pudo iniciar el procesamiento");
        return;
      }

      const jobId = submitData.job_id;

      // 2) Poll every 3s, max 3 minutes
      const POLL_INTERVAL_MS = 3000;
      const MAX_POLLS = 60;
      let finalData = null;

      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        try {
          const poll = await fetch(`${apiUrl}/api/arts/job/${jobId}`);
          const pollText = await poll.text();
          let pollData = null;
          try { pollData = pollText ? JSON.parse(pollText) : null; } catch (_e) { pollData = null; }
          if (pollData?.status === "done") {
            finalData = pollData.result;
            break;
          }
          if (pollData?.status === "error") {
            toast.dismiss(loadingToast);
            toast.error(pollData.error || "La IA no pudo procesar la imagen.");
            return;
          }
        } catch (_pollErr) {
          // network blip, keep polling
        }
      }

      toast.dismiss(loadingToast);

      if (finalData && finalData.success) {
        setProcessedPreview(finalData.image_base64);
        setProcessedFilename(finalData.filename);

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => setForegroundImage(img);
        img.src = finalData.image_base64;

        const modelName = finalData.model?.includes("pro") ? "Pro" : "Flash";
        toast.success(`¡Fondo reemplazado con Gemini ${modelName}!`);
      } else {
        toast.error("La IA tardó demasiado. Intenta de nuevo.", { duration: 8000 });
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      if (error.name === "AbortError") {
        toast.error("La IA tardó demasiado. Intenta con calidad Flash o una imagen más pequeña.");
      } else {
        toast.error("Error: " + (error.message || "desconocido"));
      }
    } finally {
      setProcessing(false);
    }
  };
  
  const handlePreviewComposite = async () => {
    if (!currentFileId) {
      toast.error("Primero sube una imagen");
      return;
    }
    
    if (!backgroundImageFile) {
      toast.error("Selecciona una imagen de fondo");
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("file_id", currentFileId);
      formData.append("bg_file", backgroundImageFile);
      formData.append("scale", scale.toString());
      formData.append("position_x", positionX.toString());
      formData.append("position_y", positionY.toString());
      formData.append("opacity", opacity.toString());
      
      const response = await axiosInstance.post("/sublimation/change-background", formData);
      
      // Solo actualizar preview, NO el filename final
      setProcessedPreview(response.data.preview);
      toast.success("Vista previa actualizada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };
  
  // Canvas drawing function for interactive preview
  const drawCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !backgroundImage || !foregroundImage) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to background image size
    canvas.width = backgroundImage.width;
    canvas.height = backgroundImage.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background
    ctx.drawImage(backgroundImage, 0, 0);
    
    // Draw grid if in drag mode
    if (dragMode) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    }
    
    // Calculate foreground dimensions with scale
    const fgWidth = foregroundImage.width * scale;
    const fgHeight = foregroundImage.height * scale;
    
    // Calculate center position with offset
    const fgX = (canvas.width - fgWidth) / 2 + positionX;
    const fgY = (canvas.height - fgHeight) / 2 + positionY;
    
    // Apply opacity
    ctx.globalAlpha = opacity / 100;
    
    // Draw foreground (scaled and positioned)
    ctx.drawImage(foregroundImage, fgX, fgY, fgWidth, fgHeight);
    
    // Reset alpha
    ctx.globalAlpha = 1.0;
    
    // Generate preview as data URL
    const previewDataUrl = canvas.toDataURL('image/png');
    setCanvasPreview(previewDataUrl);
  }, [backgroundImage, foregroundImage, scale, positionX, positionY, opacity, dragMode, gridSize]);
  
  // Redraw canvas when parameters change
  React.useEffect(() => {
    if (dragMode && backgroundImage && foregroundImage) {
      drawCanvas();
    }
  }, [dragMode, backgroundImage, foregroundImage, scale, positionX, positionY, opacity, drawCanvas]);
  
  // Canvas mouse handlers
  const handleCanvasMouseDown = (e) => {
    if (!dragMode) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;
    
    setIsDragging(true);
    setDragStart({ x: mouseX - positionX, y: mouseY - positionY });
  };
  
  const handleCanvasMouseMove = (e) => {
    if (!dragMode || !isDragging) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;
    
    let newX = mouseX - dragStart.x;
    let newY = mouseY - dragStart.y;
    
    // Snap to grid
    newX = Math.round(newX / gridSize) * gridSize;
    newY = Math.round(newY / gridSize) * gridSize;
    
    setPositionX(newX);
    setPositionY(newY);
  };
  
  const handleCanvasMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleCanvasWheel = (e) => {
    if (!dragMode) return;
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.max(0.1, Math.min(3, scale + delta));
    setScale(newScale);
  };
  
  
  const handleChangeBackgroundImage = async () => {
    if (!currentFileId) {
      toast.error("Primero sube una imagen");
      return;
    }
    
    if (!backgroundImageFile) {
      toast.error("Selecciona una imagen de fondo");
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("file_id", currentFileId);
      formData.append("bg_file", backgroundImageFile);
      formData.append("scale", scale.toString());
      formData.append("position_x", positionX.toString());
      formData.append("position_y", positionY.toString());
      formData.append("opacity", opacity.toString());
      
      const response = await axiosInstance.post("/sublimation/change-background", formData);
      
      setProcessedPreview(response.data.preview);
      setProcessedFilename(response.data.processed_filename);
      toast.success("Imagen compuesta correctamente - Lista para descargar");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };
  
  const handleResize = async () => {
    if (!currentFileId) {
      toast.error("Primero sube una imagen");
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("file_id", currentFileId);
      formData.append("product_type", productType);
      formData.append("dpi", dpi.toString());
      
      if (productType === "custom") {
        formData.append("custom_width", customWidth.toString());
        formData.append("custom_height", customHeight.toString());
      }
      
      const response = await axiosInstance.post("/sublimation/resize", formData);
      
      setProcessedPreview(response.data.preview);
      setProcessedFilename(response.data.processed_filename);
      toast.success(`Imagen redimensionada para ${productType}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };
  
  const handleEnhance = async () => {
    if (!currentFileId) {
      toast.error("Primero sube una imagen");
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("file_id", currentFileId);
      formData.append("sharpen", sharpen.toString());
      formData.append("contrast", contrast.toString());
      formData.append("brightness", brightness.toString());
      
      const response = await axiosInstance.post("/sublimation/enhance", formData);
      
      setProcessedPreview(response.data.preview);
      setProcessedFilename(response.data.processed_filename);
      toast.success("Imagen mejorada correctamente");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };
  
  const handleDownload = async () => {
    if (!processedFilename) {
      toast.error("No hay archivo procesado para descargar");
      return;
    }
    
    try {
      const response = await axiosInstance.get(`/sublimation/download/${processedFilename}`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", processedFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Descarga iniciada");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  
  const handleDownloadAsPDF = async () => {
    if (!processedFilename && !currentFileId) {
      toast.error("Primero procesa una imagen");
      return;
    }

    setProcessing(true);
    try {
      // Use the file_id from the latest processed image (e.g. "<uuid>_art.png" from AI replace bg)
      // Fallback to currentFileId for older flows (remove-bg, color, enhance, resize)
      let fileIdToConvert = currentFileId;
      if (processedFilename) {
        const m = processedFilename.match(/^([0-9a-fA-F-]{36})_/);
        if (m) fileIdToConvert = m[1];
      }

      const formData = new FormData();
      formData.append("file_id", fileIdToConvert);

      const convertResponse = await axiosInstance.post("/sublimation/convert-to-pdf", formData);
      const pdfFilename = convertResponse.data.pdf_filename;

      const response = await axiosInstance.get(`/sublimation/download/${pdfFilename}`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", pdfFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF descargado correctamente");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Sublimación</h1>
      </div>
      
      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="upload">
            <Upload className="mr-2 h-4 w-4" />
            Subir
          </TabsTrigger>
          <TabsTrigger value="background">
            <ImageMinus className="mr-2 h-4 w-4" />
            Fondo
          </TabsTrigger>
          <TabsTrigger value="enhance">
            <Palette className="mr-2 h-4 w-4" />
            Mejorar
          </TabsTrigger>
          <TabsTrigger value="resize">
            <Maximize2 className="mr-2 h-4 w-4" />
            Tamaño
          </TabsTrigger>
          <TabsTrigger value="preview">
            <FileImage className="mr-2 h-4 w-4" />
            Descargar
          </TabsTrigger>
        </TabsList>
        
        {/* UPLOAD TAB */}
        <TabsContent value="upload">
          <Card>
            <CardHeader>
              <CardTitle>Subir Imagen</CardTitle>
              <CardDescription>
                Sube una imagen, foto o PDF para comenzar el procesamiento
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="file">Seleccionar archivo</Label>
                <Input
                  id="file"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileSelect}
                />
              </div>
              
              {originalPreview && (
                <div className="border rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Vista previa:</p>
                  <img 
                    src={originalPreview} 
                    alt="Preview" 
                    className="max-w-full h-auto max-h-96 mx-auto"
                  />
                </div>
              )}
              
              <Button 
                onClick={handleUpload} 
                disabled={!selectedFile || processing}
                className="w-full"
              >
                {processing ? "Subiendo..." : "Subir Imagen"}
              </Button>
              
              {currentFileId && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    ✓ Imagen subida: {currentFilename}
                  </p>
                  <p className="text-xs text-green-600">
                    ID: {currentFileId}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* BACKGROUND TAB */}
        <TabsContent value="background">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* LEFT PANEL - Controls */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>1. Quitar Fondo</CardTitle>
                  <CardDescription>
                    Remueve el fondo, dejando solo personas/animales
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={handleRemoveBackground} 
                    disabled={!currentFileId || processing}
                    className="w-full"
                  >
                    <ImageMinus className="mr-2 h-4 w-4" />
                    {processing ? "Procesando..." : "Quitar Fondo"}
                  </Button>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>1B. Quitar Personas/Animales</CardTitle>
                  <CardDescription>
                    Elimina personas/animales, dejando solo el fondo
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={handleRemoveSubject} 
                    disabled={!currentFileId || processing}
                    className="w-full"
                    variant="outline"
                  >
                    <ImageMinus className="mr-2 h-4 w-4" />
                    {processing ? "Procesando..." : "Eliminar Personas/Animales"}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    ⚠️ Opuesto a "Quitar Fondo": te quedas con el fondo, sin personas/animales
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>2A. Color Sólido (Opcional)</CardTitle>
                  <CardDescription>
                    O agrega un color de fondo sólido
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bgColor">Color de fondo</Label>
                    <div className="flex gap-2">
                      <Input
                        id="bgColor"
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        placeholder="#FFFFFF"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleChangeBackgroundColor} 
                    disabled={!currentFileId || processing}
                    className="w-full"
                  >
                    <Palette className="mr-2 h-4 w-4" />
                    {processing ? "Procesando..." : "Aplicar Color"}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>🎯 2B. Solo Persona (quitar objetos)</span>
                  </CardTitle>
                  <CardDescription>
                    La IA elimina todos los objetos alrededor (sillas, bandejas, comida, muebles) y deja solo a la persona/bebé/animal con <strong>fondo transparente</strong>, listo para usar como sticker de WhatsApp.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    onClick={handleIsolateSubject}
                    disabled={!selectedFile || processing}
                    className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                    data-testid="sub-isolate-subject-btn"
                  >
                    🎯 {processing ? "Procesando con IA..." : "Aislar solo al sujeto principal"}
                  </Button>
                  <p className="text-xs text-amber-700 bg-amber-100 border border-amber-200 rounded p-2">
                    💡 Ideal cuando hay objetos delante o alrededor (silla alta, bandeja, mesa, juguetes). La IA reconstruye partes ocultas del sujeto y deja fondo transparente (PNG). Calidad Pro (~30-50s).
                  </p>
                </CardContent>
              </Card>

              <Card className="border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>✨ 2C. Reemplazar Fondo con IA</span>
                  </CardTitle>
                  <CardDescription>
                    Misma calidad que Artes: la IA conserva el sujeto y construye un escenario profesional según tu descripción.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label>Plantillas rápidas</Label>
                    <div className="grid grid-cols-2 gap-1 max-h-44 overflow-y-auto p-1 border rounded-md bg-white/60">
                      {subBgPresets.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => setAiBgPrompt(p.prompt)}
                          disabled={processing}
                          className="text-xs text-left px-2 py-1.5 rounded bg-white border border-purple-200 hover:border-purple-500 hover:bg-purple-100 transition-colors disabled:opacity-50"
                          data-testid={`sub-ai-bg-preset-${p.label}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-purple-700">Toca una plantilla y edítala si quieres.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="aiBgPrompt">Describe el nuevo fondo</Label>
                    <textarea
                      id="aiBgPrompt"
                      data-testid="sub-ai-bg-prompt"
                      value={aiBgPrompt}
                      onChange={(e) => setAiBgPrompt(e.target.value)}
                      placeholder="Ej: Estudio fotográfico con globos rosados, flores y un letrero de Felices 10 Meses"
                      className="w-full min-h-[70px] text-sm rounded-md border border-input bg-background px-3 py-2"
                      disabled={processing}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>📐 Formato / Producto</Label>
                    <Select value={aiBgFormat} onValueChange={setAiBgFormat} disabled={processing}>
                      <SelectTrigger data-testid="sub-ai-bg-format"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {aiBgFormats.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-purple-700">La IA ajustará el fondo a la proporción del producto que elijas.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Calidad IA</Label>
                    <Select value={aiBgQuality} onValueChange={setAiBgQuality} disabled={processing}>
                      <SelectTrigger data-testid="sub-ai-bg-quality"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">🏆 Pro (mejor calidad, ~30-50s)</SelectItem>
                        <SelectItem value="fast">⚡ Flash (~15-25s)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleReplaceBackgroundAI}
                    disabled={!selectedFile || processing || !aiBgPrompt.trim()}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="sub-ai-bg-btn"
                  >
                    <Palette className="mr-2 h-4 w-4" />
                    {processing ? "Procesando con IA..." : "Reemplazar fondo con IA"}
                  </Button>
                  <p className="text-xs text-purple-700">
                    💡 La imagen original (subida en la pestaña "Subir") se envía a Gemini Nano Banana. El resultado aparecerá en la vista previa.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>2B. Pegar en Otra Imagen (Opcional)</CardTitle>
                  <CardDescription>
                    O pega sobre una imagen personalizada
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bgImage">Seleccionar imagen de fondo</Label>
                    <Input
                      id="bgImage"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setBackgroundImageFile(file);
                        
                        // Load background image for canvas
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const img = new Image();
                            img.crossOrigin = "anonymous";
                            img.onload = () => setBackgroundImage(img);
                            img.src = event.target.result;
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </div>
                  
                  <div className="border-t pt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Modo de Posicionamiento:</p>
                      <Button
                        onClick={() => setDragMode(!dragMode)}
                        variant={dragMode ? "default" : "outline"}
                        size="sm"
                      >
                        {dragMode ? "🎯 Modo Arrastre ACTIVO" : "🖱️ Activar Arrastre"}
                      </Button>
                    </div>
                    
                    {dragMode && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
                        <p className="text-sm font-medium text-blue-900">
                          💡 Instrucciones:
                        </p>
                        <ul className="text-xs text-blue-800 space-y-1">
                          <li>• Arrastra la imagen con el mouse</li>
                          <li>• Se ajustará automáticamente a la cuadrícula</li>
                          <li>• Usa la rueda del mouse para escalar (opcional)</li>
                        </ul>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="scale">Escala: {scale}x</Label>
                      <Input
                        id="scale"
                        type="range"
                        min="0.1"
                        max="3"
                        step="0.1"
                        value={scale}
                        onChange={(e) => setScale(parseFloat(e.target.value))}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="posX">Posición X: {positionX}</Label>
                        <Input
                          id="posX"
                          type="range"
                          min="-500"
                          max="500"
                          step="10"
                          value={positionX}
                          onChange={(e) => setPositionX(parseInt(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="posY">Posición Y: {positionY}</Label>
                        <Input
                          id="posY"
                          type="range"
                          min="-500"
                          max="500"
                          step="10"
                          value={positionY}
                          onChange={(e) => setPositionY(parseInt(e.target.value))}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="opacity">Opacidad: {opacity}%</Label>
                      <Input
                        id="opacity"
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={opacity}
                        onChange={(e) => setOpacity(parseInt(e.target.value))}
                      />
                    </div>
                    
                    <Button
                      onClick={() => {
                        setScale(1.0);
                        setPositionX(0);
                        setPositionY(0);
                        setOpacity(100);
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      Resetear ajustes
                    </Button>
                  </div>
                  
                  <div className="space-y-2 border-t pt-4">
                    <Button 
                      onClick={handlePreviewComposite} 
                      disabled={!currentFileId || !backgroundImageFile || processing}
                      variant="outline"
                      className="w-full"
                    >
                      <FileImage className="mr-2 h-4 w-4" />
                      {processing ? "Generando..." : "👁️ Ver Vista Previa"}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground text-center">
                      Usa "Vista Previa" para ver cómo queda antes de guardar
                    </p>
                    
                    <Button 
                      onClick={handleChangeBackgroundImage} 
                      disabled={!currentFileId || !backgroundImageFile || processing}
                      className="w-full"
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      {processing ? "Procesando..." : "✓ Guardar Final"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* RIGHT PANEL - Live Preview */}
            <div className="space-y-4">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle>Vista Previa en Tiempo Real</CardTitle>
                  <CardDescription>
                    {dragMode && backgroundImage && foregroundImage 
                      ? "Arrastra la imagen para posicionarla" 
                      : processedPreview 
                      ? "Resultado actual" 
                      : "Procesa una imagen para ver la vista previa"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {dragMode && backgroundImage && foregroundImage ? (
                    <div className="space-y-4">
                      <div className="border-2 border-blue-300 rounded-lg p-2 bg-slate-50">
                        <canvas
                          ref={canvasRef}
                          onMouseDown={handleCanvasMouseDown}
                          onMouseMove={handleCanvasMouseMove}
                          onMouseUp={handleCanvasMouseUp}
                          onMouseLeave={handleCanvasMouseUp}
                          onWheel={handleCanvasWheel}
                          className="max-w-full h-auto mx-auto cursor-move"
                          style={{ 
                            maxHeight: '500px',
                            imageRendering: 'auto'
                          }}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Posición X: {positionX}px</span>
                          <span>Posición Y: {positionY}px</span>
                          <span>Escala: {scale.toFixed(1)}x</span>
                        </div>
                        
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <p className="text-xs text-green-800">
                            ✓ Cuando estés satisfecho, haz clic en "✓ Guardar Final" abajo
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : processedPreview ? (
                    <div className="space-y-4">
                      <div className="border rounded-lg p-2 bg-slate-50">
                        <img 
                          src={processedPreview} 
                          alt="Vista previa" 
                          className="max-w-full h-auto mx-auto"
                          style={{ maxHeight: '500px' }}
                        />
                      </div>
                      
                      {processedFilename && (
                        <div className="text-xs text-muted-foreground text-center">
                          Archivo: {processedFilename}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-12 text-center">
                      <FileImage className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">
                        {!foregroundImage 
                          ? "Primero quita el fondo de una imagen"
                          : !backgroundImage
                          ? "Selecciona una imagen de fondo"
                          : "Activa el modo arrastre para comenzar"}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        

        {/* ENHANCE TAB */}
        <TabsContent value="enhance">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* LEFT PANEL - Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Mejorar Calidad de Imagen</CardTitle>
                <CardDescription>
                  Ajusta nitidez, contraste y brillo. La vista previa se actualizará al aplicar
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sharpen">Nitidez: {sharpen}</Label>
                  <Input
                    id="sharpen"
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={sharpen}
                    onChange={(e) => setSharpen(parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Aumenta la nitidez para hacer la imagen más definida
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contrast">Contraste: {contrast > 0 ? '+' : ''}{contrast}</Label>
                  <Input
                    id="contrast"
                    type="range"
                    min="-100"
                    max="100"
                    step="5"
                    value={contrast}
                    onChange={(e) => setContrast(parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Ajusta la diferencia entre luces y sombras
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="brightness">Brillo: {brightness > 0 ? '+' : ''}{brightness}</Label>
                  <Input
                    id="brightness"
                    type="range"
                    min="-100"
                    max="100"
                    step="5"
                    value={brightness}
                    onChange={(e) => setBrightness(parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Aumenta o reduce la luminosidad general
                  </p>
                </div>
                
                <Button
                  onClick={() => {
                    setSharpen(0);
                    setContrast(0);
                    setBrightness(0);
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  Resetear mejoras
                </Button>
                
                <Button 
                  onClick={handleEnhance} 
                  disabled={!currentFileId || processing}
                  className="w-full"
                >
                  <Palette className="mr-2 h-4 w-4" />
                  {processing ? "Procesando..." : "Aplicar Mejoras"}
                </Button>
              </CardContent>
            </Card>
            
            {/* RIGHT PANEL - Live Preview */}
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Vista Previa en Tiempo Real</CardTitle>
                <CardDescription>
                  {processedPreview ? "Resultado actual con mejoras" : "Aplica mejoras para ver la vista previa"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {processedPreview ? (
                  <div className="space-y-4">
                    <div className="border rounded-lg p-2 bg-slate-50">
                      <img 
                        src={processedPreview} 
                        alt="Vista previa" 
                        className="max-w-full h-auto mx-auto"
                        style={{ maxHeight: '500px' }}
                      />
                    </div>
                    
                    {processedFilename && (
                      <div className="text-xs text-muted-foreground text-center">
                        Archivo: {processedFilename}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-12 text-center">
                    <FileImage className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      La vista previa aparecerá aquí
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* RESIZE TAB */}
        <TabsContent value="resize">
          <Card>
            <CardHeader>
              <CardTitle>Redimensionar para Producto</CardTitle>
              <CardDescription>
                Ajusta el tamaño de la imagen según el producto de sublimación
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="productType">Tamaño de hoja / Producto</Label>
                <Select value={productType} onValueChange={setProductType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carta">📄 Carta (21.6 × 27.9 cm)</SelectItem>
                    <SelectItem value="carta_horizontal">📄 Carta Horizontal (27.9 × 21.6 cm)</SelectItem>
                    <SelectItem value="oficio">📋 Oficio (21.6 × 35.6 cm)</SelectItem>
                    <SelectItem value="oficio_horizontal">📋 Oficio Horizontal (35.6 × 21.6 cm)</SelectItem>
                    <SelectItem value="a4">📃 A4 (21 × 29.7 cm)</SelectItem>
                    <SelectItem value="a4_horizontal">📃 A4 Horizontal (29.7 × 21 cm)</SelectItem>
                    <SelectItem value="a3">📰 A3 (29.7 × 42 cm)</SelectItem>
                    <SelectItem value="a3_horizontal">📰 A3 Horizontal (42 × 29.7 cm)</SelectItem>
                    <SelectItem value="tabloide">🗞️ Tabloide (27.9 × 43.2 cm)</SelectItem>
                    <SelectItem value="tabloide_horizontal">🗞️ Tabloide Horizontal (43.2 × 27.9 cm)</SelectItem>
                    <SelectItem value="taza_11oz">☕ Taza 11oz (9.5 × 20 cm)</SelectItem>
                    <SelectItem value="taza_15oz">☕ Taza 15oz (10 × 22 cm)</SelectItem>
                    <SelectItem value="almohada_40">🛋️ Cojín 40×40 cm</SelectItem>
                    <SelectItem value="almohada_45">🛋️ Cojín 45×45 cm</SelectItem>
                    <SelectItem value="mousepad">🖱️ Mousepad (24 × 20 cm)</SelectItem>
                    <SelectItem value="camiseta_front">👕 Camiseta Frente (28 × 35.5 cm)</SelectItem>
                    <SelectItem value="camiseta_back">👕 Camiseta Espalda (28 × 35.5 cm)</SelectItem>
                    <SelectItem value="gorra">🧢 Gorra (15 × 7.6 cm)</SelectItem>
                    <SelectItem value="custom">📐 Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {productType === "custom" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customWidth">Ancho (cm)</Label>
                    <Input
                      id="customWidth"
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(parseFloat(e.target.value))}
                      min="1"
                      step="0.5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customHeight">Alto (cm)</Label>
                    <Input
                      id="customHeight"
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(parseFloat(e.target.value))}
                      min="1"
                      step="0.5"
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="dpi">DPI (calidad)</Label>
                <Select value={dpi.toString()} onValueChange={(v) => setDpi(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="150">150 DPI (Borrador)</SelectItem>
                    <SelectItem value="300">300 DPI (Estándar)</SelectItem>
                    <SelectItem value="600">600 DPI (Alta calidad)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={handleResize} 
                disabled={!currentFileId || processing}
                className="w-full"
              >
                <Maximize2 className="mr-2 h-4 w-4" />
                {processing ? "Redimensionando..." : "Redimensionar"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* DOWNLOAD TAB */}
        <TabsContent value="preview">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Imagen Original</CardTitle>
              </CardHeader>
              <CardContent>
                {originalPreview ? (
                  <img 
                    src={originalPreview} 
                    alt="Original" 
                    className="max-w-full h-auto max-h-96 mx-auto border rounded-lg"
                  />
                ) : (
                  <p className="text-center text-muted-foreground py-12">
                    No hay imagen subida
                  </p>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Imagen Procesada y Opciones de Descarga</CardTitle>
              </CardHeader>
              <CardContent>
                {processedPreview ? (
                  <div className="space-y-4">
                    <img 
                      src={processedPreview} 
                      alt="Processed" 
                      className="max-w-full h-auto max-h-96 mx-auto border rounded-lg"
                    />
                    
                    {processedFilename && (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Archivo: {processedFilename}
                        </p>
                        
                        <div className="border-t pt-3 space-y-2">
                          <p className="text-sm font-medium">Descargar como:</p>
                          
                          <Button 
                            onClick={handleDownload}
                            className="w-full"
                            variant="default"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Descargar Imagen (Original)
                          </Button>
                          
                          <Button 
                            onClick={handleDownloadAsPDF}
                            className="w-full"
                            variant="outline"
                            disabled={processing}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {processing ? "Convirtiendo..." : "Descargar como PDF"}
                          </Button>

                          <Button
                            onClick={handleSendAsSticker}
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            data-testid="sub-send-sticker-btn"
                          >
                            📲 Enviar como sticker WhatsApp
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">
                    No hay imagen procesada aún.
                    <br />
                    <br />
                    Sube una imagen y procésala para poder descargar.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
